import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { anodum, i18next, site, view, hdrm, dialogs, integration } from '@yola/ws-sdk';
import { constants } from '@yola/subscription-manager-js';
import bowser from 'yola-bowser';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import useDialogPosition from 'src/js/modules/dialogs/hooks/use-dialog-position';
import setDisabledStatus from 'src/js/modules/view/actions/set-disabled-status';
import upsell from 'src/js/modules/upsell';
import ai from '../../ai';
import BlockGenerationDialog, { BASE_DIALOG_HEIGHT } from '../components/block-generation-dialog';
import useBlockGenerationForm from '../hooks/use-block-generation-form';
import getUsedImageSources from '../helpers/get-used-image-sources';
import { fieldNames } from '../constants/block-generation';
import { DIALOG_WIDTH } from '../constants/common';

const {
  actions: { setNumberOfAiGeneratedBlocks },
  constants: { siteProperties: sitePropertiesNames, sourceIds },
  helpers: { initiateBlockGeneration },
  hooks: { useAiOnboarding },
  selectors: { getNumberOfAiGeneratedBlocks },
} = ai;
const {
  hostingPackageFeatureNames: { AI_BLOCK_GENERATION_QUOTA },
} = constants;

const HTTP_CONFLICT = 409;

const isQuotaExceededError = (error) =>
  error.response?.status === HTTP_CONFLICT &&
  error.response?.data?.code === 'ai_block_generation_quota_exceeded';

const getCaptions = (captionsOverrides) => ({
  upgradeBanner: {
    title: `${i18next.t('Blocks generated')}:`,
    buttonText: i18next.t('Get more'),
  },
  loadingStatus: i18next.t('Generating block...'),
  ...captionsOverrides,
});

function BlockGenerationContainer({
  element,
  initialBlockCategory,
  captions: captionsOverrides,
  initialUserInput = '',
  initialSize = { width: null, height: null },
  initialOffset = { x: 0, y: 0 },
}) {
  const dispatch = useDispatch();

  const [dialogSize, setDialogSize] = useState({
    width: initialSize.width || DIALOG_WIDTH,
    height: initialSize.height || BASE_DIALOG_HEIGHT,
  });

  const activePageId = useSelector(site.selectors.getActivePageId);
  const { locale } = useSelector((state) => site.selectors.getPage(state, activePageId));
  const limits = useSelector(integration.selectors.getLimits);
  const siteProperties = useSelector(site.selectors.getSiteProperties);
  const upsells = useSelector(integration.selectors.getUpsells);
  const sessionFeatureUsage = useSelector(getNumberOfAiGeneratedBlocks);

  const { fields, handleSubmit, isSubmitDisabled, getFieldState } = useBlockGenerationForm(
    initialBlockCategory,
    initialUserInput
  );
  const { onNextDialog } = useAiOnboarding();
  const { modalRef, modalOffset, onModalReady, onModalDragEnd } = useDialogPosition({
    element,
    initialOffsetX: initialOffset.x,
    initialOffsetY: initialOffset.y,
  });

  const {
    upgradeBanner: upgradeBannerCaptions,
    loadingStatus,
    ...captions
  } = getCaptions(captionsOverrides);

  const featureLimit = get(limits, AI_BLOCK_GENERATION_QUOTA, {});
  const featureUsage =
    sessionFeatureUsage ??
    get(siteProperties, sitePropertiesNames.NUMBER_OF_AI_GENERATED_BLOCKS_PROPERTY_NAME, 0);
  const upgradeBanner = featureLimit.upgradeAvailable
    ? {
        current: featureUsage,
        total: featureLimit.amount,
        captions: upgradeBannerCaptions,
        onButtonClick: () => {
          upsells.aiBlockGenerationQuota.onUpgrade().catch(() => {
            // eslint-disable-next-line no-console
            console.log('Upgrade flow was canceled');
          });
        },
      }
    : null;

  const handleCancel = () => {
    dispatch(dialogs.actions.hide());
  };

  const updateResizedDialogSize = (newWidth, newHeight) => {
    setDialogSize({ width: newWidth, height: newHeight });
  };

  const reopenDialog = useCallback(
    (blockCategory, userInput) => {
      dispatch(
        dialogs.actions.show(dialogTypes.BLOCK_GENERATION_DIALOG, {
          element,
          initialBlockCategory: blockCategory,
          captions,
          initialUserInput: userInput,
          initialSize: dialogSize,
          initialOffset: modalOffset,
        })
      );
    },
    [captions, dialogSize, dispatch, element, modalOffset]
  );

  const generateBlock = useCallback(
    async (selectedBlockCategory, userInput) => {
      dispatch(dialogs.actions.hide());
      dispatch(view.actions.setLoadedStatus(loadingStatus));
      dispatch(setDisabledStatus(true));

      try {
        const usedImageSources = getUsedImageSources(element);
        const shouldSendUserInput = !!userInput && getFieldState(fieldNames.USER_INPUT).isTouched;

        const response = await initiateBlockGeneration({
          blockCategory: selectedBlockCategory,
          locale,
          ...(shouldSendUserInput && { userInput }),
          ...(usedImageSources.length && { usedImageSources }),
        });
        const { html: generatedHtml, numberOfAiGeneratedBlocks } = response.results;
        const generatedBlock = anodum.parseElementFromString(generatedHtml);
        const elementId = view.accessors.getLiveElementId(element);

        dispatch(setNumberOfAiGeneratedBlocks(numberOfAiGeneratedBlocks));
        dispatch(
          view.actions.replaceElement(generatedBlock, elementId, {
            strategy: hdrm.constants.updateStrategies.UPDATE_STATIC_ONLY,
          })
        );

        if (!bowser.mobile) {
          reopenDialog(selectedBlockCategory, userInput);
        }
      } catch (e) {
        if (isQuotaExceededError(e)) {
          upsell.operations
            .requestFeatureAvailability(AI_BLOCK_GENERATION_QUOTA, { ...featureLimit })
            .then(() => {
              generateBlock(selectedBlockCategory, userInput);
            })
            .catch(() => {
              // eslint-disable-next-line no-console
              console.log('Upgrade flow was canceled');
            });
        } else {
          console.error(e);
          dispatch(
            dialogs.actions.show(dialogTypes.AI_ERROR_DIALOG, {
              errorType: e.results?.code,
            })
          );
        }
      } finally {
        dispatch(view.actions.setLoadedStatus(true));
        dispatch(setDisabledStatus(false));
      }
    },
    [dispatch, element, featureLimit, getFieldState, loadingStatus, locale, reopenDialog]
  );

  const handleDialogSubmit = useCallback(
    (data) => {
      onNextDialog({
        sourceId: sourceIds.GENERATE_BLOCK,
        title: captions.title,
        onNext: () => {
          const selectedBlockCategory = data[fieldNames.BLOCK_CATEGORY];
          const userInput = data[fieldNames.USER_INPUT] ? data[fieldNames.USER_INPUT].trim() : '';
          generateBlock(selectedBlockCategory, userInput);
        },
      });
    },
    [captions.title, generateBlock, onNextDialog]
  );

  return (
    <BlockGenerationDialog
      modalRef={modalRef}
      captions={captions}
      fields={fields}
      isSubmitDisabled={isSubmitDisabled}
      offset={modalOffset}
      size={dialogSize}
      onSubmit={handleSubmit(handleDialogSubmit)}
      onCancel={handleCancel}
      onReady={onModalReady}
      onDragEnd={onModalDragEnd}
      onResizeStop={updateResizedDialogSize}
      upgradeBanner={upgradeBanner}
    />
  );
}

BlockGenerationContainer.propTypes = {
  element: PropTypes.object.isRequired,
  initialBlockCategory: PropTypes.string.isRequired,
  captions: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.node.isRequired,
    buttonLabel: PropTypes.string.isRequired,
  }).isRequired,
  initialUserInput: PropTypes.string,
  initialSize: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  initialOffset: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
};

export default BlockGenerationContainer;
